import { toast } from "sonner";
import { AnalyticsEvents, useAnalytics } from "@/utils/analytics";
import { graphql, useFragment, useMutation } from "react-relay";
import type { eventCard$key } from "./__generated__/eventCard.graphql";
import { Card } from "@/components/ui/card";
import { format, isBefore } from "date-fns";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Icon } from "@/components/icon";
import type { eventCardConfirmAttendanceMutation } from "./__generated__/eventCardConfirmAttendanceMutation.graphql";
import type { eventCardUnConfirmAttendanceMutation } from "./__generated__/eventCardUnConfirmAttendanceMutation.graphql";
import { useRouteContext, Link } from "@tanstack/react-router";
import { TrashIcon, PencilIcon } from "@heroicons/react/16/solid";
import { cn } from "@/utils/cn";
import { Image } from "@/components/image";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { ptBR } from "date-fns/locale";

export function EventCard({ event: eventFragment }: { event: eventCard$key }) {
	const { club } = useRouteContext({ from: "__root__" });
	const { track } = useAnalytics();
	const event = useFragment<eventCard$key>(
		graphql`
      fragment eventCard on Event {
        id
        title
        slug
        startDate
        endDate
        eventUrl
        timezone
        attendeeCount
        address
        type
        coverPhoto {
          signedUrl
        }
        attendee {
          status
        }
          host {
          displayName
          photo {
            signedUrl
          }
          avatarFallback
        }
      }
  `,
		eventFragment,
	);
	const [confirmAttendance, isConfirming] =
		useMutation<eventCardConfirmAttendanceMutation>(graphql`
	    mutation eventCardConfirmAttendanceMutation($slug: String!, $club: String!) {
				confirmPresence(club: $club, slug: $slug) {
					id
					attendee {
						status
					}
					...eventCard
					...eventSummaryFragment
					...portals_eventPortal
					...eventFragment
				}
			}
	`);
	const [unConfirmAttendance, isUnConfirming] =
		useMutation<eventCardUnConfirmAttendanceMutation>(graphql`
	    mutation eventCardUnConfirmAttendanceMutation($slug: String!, $club: String!) {
				unconfirmPresence(club: $club, slug: $slug) {
					id
					attendee {
						status
					}
					...eventCard
					...eventSummaryFragment
					...portals_eventPortal
					...eventFragment
				}
			}
	`);
	const [deleteEvent, isDeleting] = useMutation(graphql`
    mutation eventCardDeleteEventMutation($id: ID!) {
      deleteEvent(id: $id) {
    		id @deleteRecord

        ...eventCard
        ...eventSummaryFragment
        ...portals_eventPortal
        ...eventFragment
    	}
    }
	`);

	return (
		<Card
			key={event.id}
			className={cn(
				"flex md:flex-row gap-6 relative overflow-hidden p-0 h-[145px]",
				isDeleting && "opacity-40",
			)}
		>
			{/* Mobile: entire card is clickable */}
			<Link
				to="/eventos/$slug"
				params={{ slug: event.slug }}
				className="absolute inset-0 md:hidden z-[1]"
				aria-label={event.title}
			/>
			{event.coverPhoto?.signedUrl ? (
				<Link
					to="/eventos/$slug"
					params={{ slug: event.slug }}
					className="h-full max-w-full aspect-video overflow-hidden"
				>
					<Image
						alt={event.title}
						src={event.coverPhoto?.signedUrl!}
						className="object-cover w-full h-full"
					/>
				</Link>
			) : (
				<Link
					to="/eventos/$slug"
					params={{ slug: event.slug }}
					className="h-full max-w-full flex flex-col items-center justify-center font-semibold aspect-video bg-muted overflow-hidden"
				>
					<span className="text-4xl">
						{format(new Date(event.startDate), "dd")}
					</span>
					<span className="text-sm text-muted-foreground uppercase">
						{format(new Date(event.startDate), "MMM", { locale: ptBR })}
					</span>
				</Link>
			)}

			<div className="flex flex-1 flex-col w-full pr-6 first:pl-6 md:py-6 max-md:pb-6 max-md:pl-6 gap-6">
				<div className=" flex flex-col md:flex-row items-start justify-between flex-1">
					<div className="flex flex-col justify-center flex-1 gap-0">
						<div className="flex items-center justify-start text-xs font-medium gap-1.5 text-muted-foreground">
							<Avatar className="size-5">
								<AvatarImage src={event.host.photo?.signedUrl} />
								<AvatarFallback>{event.host.avatarFallback}</AvatarFallback>
							</Avatar>

							<span className="truncate">{event.host?.displayName}</span>

							<Badge variant="secondary" className="border-0 rounded-md">
								{format(new Date(event.startDate), "HH:mm")}
							</Badge>
						</div>
						<div className="mt-1">
							<Link
								className="font-semibold line-clamp-1 text-base text-foreground leading-tight"
								to="/eventos/$slug"
								params={{ slug: event.slug }}
							>
								{event.title}
							</Link>

							{event.type === "IN_PERSON" && (
								<span className="text-sm leading-none line-clamp-1 text-muted-foreground">
									Evento Presencial: {event.address}
								</span>
							)}
						</div>
					</div>

					<div className="flex flex-row gap-2">
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									size="icon"
									variant="ghost"
									onClick={(ev) => {
										ev.stopPropagation();
									}}
									className="pointer-events-auto max-md:hidden"
								>
									<Icon
										name="EllipsisVertical"
										className="size-5 text-muted-foreground"
									/>
								</Button>
							</DropdownMenuTrigger>

							<DropdownMenuContent>
								<Link to="/eventos/$slug/editar" params={{ slug: event.slug }}>
									<DropdownMenuItem>
										<PencilIcon className="size-4" />
										Editar Evento
									</DropdownMenuItem>
								</Link>

								<DropdownMenuItem
									onClick={() => {
										if (
											confirm(
												"Tem certeza que desja excluir este event? Essa operação não poderá ser desfeita.",
											)
										) {
											deleteEvent({
												variables: { id: event.id },
												onCompleted: () => {
													track(AnalyticsEvents.EVENT_DELETED, {
														event_id: event.id,
													});
													toast.success("Evento deletado com sucesso!");
												},
											});
										}
									}}
								>
									<TrashIcon className="size-4" />
									Excluir Evento
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				</div>

				<div className="flex flex-row items-center justify-end max-md:hidden">
					{!event.attendee &&
						isBefore(new Date(), new Date(event.startDate)) && (
							<div>
								<Button
									disabled={isConfirming}
									onClick={() =>
										confirmAttendance({
											variables: { slug: event.slug, club: club?.slug },
											onCompleted: (response) => {
												track(AnalyticsEvents.EVENT_RSVP_CONFIRMED, {
													event_id: response.confirmPresence.id,
												});
											},
										})
									}
								>
									Quero Ir
								</Button>{" "}
							</div>
						)}

					{event.attendee && (
						<div className="flex items-center gap-2">
							<Select
								value={event.attendee.status}
								disabled={isConfirming || isUnConfirming}
								onValueChange={(value) => {
									if (value === "going") {
										return confirmAttendance({
											variables: { slug: event.slug, club: club?.slug },
											onCompleted: (response) => {
												track(AnalyticsEvents.EVENT_RSVP_CONFIRMED, {
													event_id: response.confirmPresence.id,
												});
											},
										});
									} else if (value === "not_going") {
										unConfirmAttendance({
											variables: { slug: event.slug, club: club?.slug },
											onCompleted: (response) => {
												track(AnalyticsEvents.EVENT_RSVP_CANCELLED, {
													event_id: response.unconfirmPresence.id,
												});
											},
										});
									}
								}}
							>
								<SelectTrigger size="sm">
									<SelectValue placeholder="Selecionar" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="going">✅ Eu vou!</SelectItem>
									<SelectItem value="not_going">❌ Não vou</SelectItem>
								</SelectContent>
							</Select>
						</div>
					)}
				</div>
			</div>
		</Card>
	);
}
